import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import api from "./services/api";
import { REACT_APP_VERCEL_ENV, REACT_APP_ENV } from "./env";

const vercelEnvironment = REACT_APP_VERCEL_ENV;
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

let environment;
let cuesURL;
let cmanURL;
let acgiURL;
let degreedURL;

if (vercelEnvironment === "production") {
  environment = 'production';
  cuesURL = "https://www.cues.org";
  cmanURL = "https://www.cumanagement.com";
  acgiURL = "https://online.cues.org/cuesssa"
  degreedURL = "https://degreed.com"
} else if (vercelEnvironment === "preview") {
  environment = 'staging';
  cuesURL = "https://stage.cues.org";
  cmanURL = "https://staging.cumanagement.com";
  acgiURL = "https://online.cues.org/cuesdevssa";
  degreedURL = "https://betatest.degreed.com"
} else if (vercelEnvironment === "lando" || REACT_APP_ENV === "lando") {
  environment = 'lando';
  cuesURL = "https://stage.cues.org";
  cmanURL = "https://staging.cumanagement.com";
  acgiURL = "https://online.cues.org/cuesdevssa";
  degreedURL = "https://betatest.degreed.com"
} else if (isLocalhost) {
  // When localhost is detected, check for lando environment variable first (override)
  if (REACT_APP_ENV === "lando") {
    environment = 'lando';
    cuesURL = "https://cues.lndo.site";
    cmanURL = "https://staging.cumanagement.com";
    acgiURL = "https://online.cues.org/cuesdevssa";
    degreedURL = "https://betatest.degreed.com"
  } else {
    environment = 'local';
    cuesURL = "https://stage.cues.org";
    cmanURL = "https://staging.cumanagement.com";
    acgiURL = "https://online.cues.org/cuesdevssa";
    degreedURL = "https://betatest.degreed.com"
  }
} else if (vercelEnvironment === undefined) {
  environment = 'local';
  cuesURL = "https://stage.cues.org";
  cmanURL = "https://staging.cumanagement.com";
  acgiURL = "https://online.cues.org/cuesdevssa";
  degreedURL = "https://betatest.degreed.com"
}

axios.defaults.withCredentials = true;

const welcomeCookie = Cookies.get("welcome-modal");
const welcomeCompleteCookie = Cookies.get("welcome-complete");

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [cmpData, setCmpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeModal, setWelcomeModal] = useState(false);
  const [welcomeComplete, setWelcomeComplete] = useState(false);
  const [loadedSections, setLoadedSections] = useState([]);

  const handleLogin = async () => {
    // Handle welcome modal logic.
    if (!welcomeCookie) {
      setWelcomeModal(true);
      // Set cookie so the modal isn't shown after the first time.
      Cookies.set("welcome-modal", "false", { expires: 3652 });
    }

    // Handle onboarding banner logic.
    if (welcomeCompleteCookie) {
      setWelcomeComplete(true);
    }

    // Don't check for session cookie during local development.
    if (environment === "local") {
      setAuthenticated(true);
    }
    
    // fetch only account_info and cmp_layout initially (minimal data needed for page structure)
    let accountInfoEndpoint = cuesURL + '/cmp/account-info';
    let layoutEndpoint = cuesURL + '/cmp/layout';

    // Fetch account info and layout in parallel using centralized API service
    // In local environment, this will return mock data automatically
    Promise.all([
      api.get(accountInfoEndpoint),
      api.get(layoutEndpoint)
    ])
      .then(function (responses) {
        const accountInfo = responses[0].data;
        const layout = responses[1].data;
        
        // Set minimal cmpData with account_info and cmp_layout
        const minimalCmpData = {
          account_info: accountInfo,
          cmp_layout: layout
        };
        
        setAuthenticated(true);
        setCmpData(minimalCmpData);
        window.cmpData = minimalCmpData;
        setLoading(false);
        
      })
      .catch(function (error) {
        
        setAuthenticated(false);
        setLoading(false);
        // Note: cmpData remains null, so App.js will show LoadingScreen or error state
      });
  };

  // Function to check if a section can load (previous section must be loaded first)
  const canSectionLoad = (sectionId) => {
    if (!cmpData || !cmpData.cmp_layout) return false;
    
    const sectionIndex = cmpData.cmp_layout.indexOf(sectionId);
    if (sectionIndex === -1) return false;
    
    // First section can always load
    if (sectionIndex === 0) return true;
    
    // Check if previous section is loaded
    const previousSection = cmpData.cmp_layout[sectionIndex - 1];
    return loadedSections.includes(previousSection);
  };

  // Function to mark a section as loaded
  const markSectionLoaded = (sectionId) => {
    setLoadedSections(prev => {
      if (!prev.includes(sectionId)) {
        return [...prev, sectionId];
      }
      return prev;
    });
  };

  const value = {
    authenticated,
    cmpData,
    loading,
    environment,
    handleLogin,
    welcomeModal,
    setWelcomeModal,
    welcomeComplete,
    setWelcomeComplete,
    cuesURL,
    cmanURL,
    acgiURL,
    degreedURL,
    canSectionLoad,
    markSectionLoaded,
    loadedSections
  };

  useEffect(() => {
    handleLogin();
  }, [authenticated]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  return useContext(AuthContext);
};

export { useAuth, AuthProvider };
